import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, signal } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable, of } from 'rxjs';

//models
import { BinderAssembly } from '@/models/binder-assembly.model';
import { CalcTotals } from '@/models/master-json.model';
import { Binder, BinderSmall, OwnerItem, RequestResponse } from '@/models/binder.model';
import { ApprovalRequest } from '@/models/approval.model';
import { Router } from '@angular/router';

//services
import { TaxDataService } from '../app/tax-data.service';
import { BinderAssembliesService } from '../app/binder-assemblies.service';
import { ContractMathService } from '../app/contract-math.service';
import { BinderDeliverablesService } from '../app/binder-deliverables.service';
import { AppStateService } from '../app/app-state.service';
import { ClientService } from './client.service';
import { FilesService } from './files.service';

@Injectable({
  providedIn: 'root'
})
export class BinderService {
//inject services
  private http: HttpClient = inject(HttpClient);
  private binderAssembliesService = inject(BinderAssembliesService);
  private contractMathService = inject(ContractMathService);
  private taxDataService = inject(TaxDataService);
  private binderDeliverablesService = inject(BinderDeliverablesService);
  private appStateService = inject(AppStateService);
  private clientService = inject(ClientService);
  private router: Router = inject(Router);
  private filesService = inject(FilesService);

//signals
  currentBinder = signal(new Binder());
  isLoading = signal(false);
  totalsData = signal(new CalcTotals());
  apiEditAccess = signal(false);

//constants
  private BASE_URL = environment.landmark_API_URL + "/binders";

//constructor
  constructor(
  ) { 
    effect(() => {
      const binder = this.currentBinder();
      const tax = this.taxDataService.taxData();
      const assemblies = this.binderAssembliesService.binderAssemblies();
      const binderId = binder.BinderID;
      if(binder && binder.Clients) {
        // console.log('transforming binder clients data', binder.Clients, binder.ClientID, binder.contacts);
        this.clientService.transformBinderClientsData(binder.Clients, binder.ClientID, binder.contacts);
      }
      if(assemblies && binder) {
        this.updateBinderTotals(binder, assemblies);
      }
      if(binderId) {
        this.filesService.getFileCategories(binderId);
      }
    });

  }

  async setCurrentBinder(binder: Binder) {
    // console.log('setting current binder', binder);
    this.currentBinder.set(binder);
    if(binder.Plans.Collection==='Founders') {
      // console.log('setting founders');
      this.binderAssembliesService.setIsFounders(true);
      this.binderAssembliesService.setBinderAssemblies(binder.BinderAssemblies);
    } else {
      // console.log('setting standard');
      this.binderAssembliesService.setIsFounders(false);
      this.binderAssembliesService.setBinderAssemblies(binder.BinderAssemblies);
    }
    
    const tax = await this.taxDataService.getTax(binder);
    this.updateBinderTotals(binder, this.binderAssembliesService.binderAssemblies());
    this.binderDeliverablesService.updateLocalData(binder.BinderDeliverables, true);
  }

  updateAssemblies(assemblies: BinderAssembly[]) {
    assemblies.forEach(assembly => {
      this.binderAssembliesService.updateAssemblyLocally(assembly.BAID, assembly);
    });
}
  patchBinder(binder: Partial<Binder>) {
    const currentBinder = this.currentBinder();
    const assemblies = this.binderAssembliesService.binderAssemblies();
    binder.BinderAssemblies = assemblies;
    if (currentBinder) {
      const updatedBinder = { ...currentBinder, ...binder } as Binder;
      this.setCurrentBinder(updatedBinder);
    } else {
      console.log('no binder to patch!')
    }
  }

  setStatus(status: number) {
    // console.log('setting status', status);
    this.currentBinder().ProjectStatus = status;

  }

  addApproval(approval: ApprovalRequest) {
    const currentBinder = this.currentBinder();
    if(currentBinder) {
      currentBinder.approvalRequests.push(approval);
      this.currentBinder.set(currentBinder);
    }
  }

  setIsLoading(isLoading: boolean) {
    this.isLoading.set(isLoading);
  }

  async updateBinderTotals(newData: Binder, assemblies?: BinderAssembly[]) {
    const collectedAssemblies = assemblies ?? this.binderAssembliesService.binderAssemblies();
    const totals = this.contractMathService.generateTotals(newData, this.taxDataService.taxData().rate, collectedAssemblies);
    this.totalsData.set(totals);
  }

  //api functions
  getBinder(binderId: number): Observable<any> {
    return this.http.get(`${this.BASE_URL}/${binderId}`);
  }

  requestBinder(binderId:number): Observable<RequestResponse>  {
    return this.http.get<RequestResponse>(`${this.BASE_URL}/request/${binderId}`);
  }

  updateBinder(binderId: number, changes: Partial<Binder> | BinderSmall): Observable<any> {
    if(this.appStateService.learningMode()) {
      return of(changes);
    }
    return this.http.put(`${this.BASE_URL}/${binderId}`, changes);
  }

  injectOwnerItems(addedOwnerItems: OwnerItem[], removedOwnerItems: OwnerItem[], patchedOwnerItems: OwnerItem[]) {
    const currentBinder = this.currentBinder();
    if(currentBinder && currentBinder.OwnerItems) {
      currentBinder.OwnerItems.push(...addedOwnerItems);
      currentBinder.OwnerItems = currentBinder.OwnerItems.filter(item => !removedOwnerItems.includes(item));
      patchedOwnerItems.forEach(item => {
        const index = currentBinder.OwnerItems!.findIndex(i => i.ItemID === item.ItemID);
        if (index !== -1) {
          currentBinder.OwnerItems![index] = item;
        }
      });
      this.currentBinder.set(currentBinder);
    } else if (currentBinder) {
      currentBinder.OwnerItems = addedOwnerItems;
      this.currentBinder.set(currentBinder);
    }
  }

  createHigharcBinder(binderData: any, templateId:number, planData: any, options: Partial<BinderAssembly>[], hsId:number): Observable<{BinderID:number}> {
    const data = {
      binderData : binderData,
      SSID: templateId,
      planData: planData,
      options: options,
      hsId: hsId
    };
    return this.http.post<{BinderID:number}>(`${this.BASE_URL}/higharc`, data);
  }

  createBinderFromTemplate(changes: any, templateId:number, hsId:number, engineeringAssembly?: any): Observable<{BinderID:number}> {
    const data = {
      binderData : changes,
      SSID: templateId,
      engineeringAssembly: engineeringAssembly
    };
    return this.http.post<{BinderID:number}>(`${this.BASE_URL}/fromtemplate`, data);
  }

  // createBinderFromUnlinked(changes: any, hsId:number, clients:any, templateId:number): Observable<{BinderID:number}> {
  //   const data = {
  //     binderData : changes,
  //     hsId: hsId,
  //     clients: clients,
  //     SSID: templateId
  //   };
  //   return this.http.post<{BinderID:number}>(`${this.BASE_URL}/fromhubspot`, data);
  // }

  // createHigharcBinderFromUnlinked(changes: any, hsId:number, clients:any, templateId:number, planData: any, options: Partial<BinderAssembly>[]): Observable<{BinderID:number}> {
  //   const data = {
  //     binderData : changes,
  //     hsId: hsId,
  //     clients: clients,
  //     SSID: templateId,
  //     planData: planData,
  //     options: options
  //   };
  //   return this.http.post<{BinderID:number}>(`${this.BASE_URL}/fromhigharcunlinked`, data);
  // }

  getFullDetails(binderId:number): Observable<Binder>  {
    return this.http.get<Binder>(`${this.BASE_URL}/fulldetails/${binderId}`);
  }

  loadBinder(binderId:number)  {
    this.requestBinder(binderId).subscribe((response) => {
      console.log('response', response);
      if (!response.viewAccess) {
                this.router.navigate(['/no-access']); // Redirect to no access page
              }
              if(response.binder) {
                this.setCurrentBinder(response.binder);
              }
              this.apiEditAccess.set(response.editAccess);
    });
  }

}
